import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_LINKS } from "@/mocks/site";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const goSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = term.trim();
    if (!keyword) {
      navigate("/category/all");
      return;
    }
    navigate(`/category/all?q=${encodeURIComponent(keyword)}`);
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/news") return location.pathname.startsWith("/news");
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-background-200/60 bg-background-50"
      }`}
    >
      <div className="shell flex h-16 items-center gap-4 md:h-[70px]">
        <Link to="/" className="group flex cursor-pointer items-center gap-2.5" aria-label="华丽的外出 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 transition-colors group-hover:bg-primary-600">
            <i className="ri-film-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-extrabold tracking-tight text-foreground-950">
              华丽的外出
            </span>
            <span className="font-label mt-1 text-[10px] font-medium uppercase tracking-[0.22em] text-foreground-400">
              Film Journal
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-0.5 lg:flex" aria-label="主导航">
          {NAV_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={goSearch} className="hidden lg:block" role="search">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
                <i className="ri-search-line text-base text-foreground-400" />
              </span>
              <input
                type="search"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className="h-9 w-40 rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-100 xl:w-52"
              />
            </div>
          </form>

          <Link
            to="/contact"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 xl:inline-flex"
          >
            反馈建议
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="anim-soft-in border-t border-background-200 bg-background-50 lg:hidden">
          <div className="shell py-4">
            <form onSubmit={goSearch} role="search" className="mb-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
                  <i className="ri-search-line text-base text-foreground-400" />
                </span>
                <input
                  type="search"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="搜索影视作品或演员"
                  aria-label="搜索影视作品或演员"
                  className="h-10 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
                />
              </div>
            </form>
            <div className="grid grid-cols-2 gap-1.5">
              {NAV_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive(item.to)
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-arrow-right-s-line text-sm opacity-50" />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}